import { Injectable } from '@angular/core';
import { SchenckColors } from '@schenck/rotec-components';

export enum ChartColors {
  BLUE = 'BLUE',
  GREEN = 'GREEN',
  RED = 'RED',
  GRAY = 'GRAY',
}

@Injectable({
  providedIn: 'root',
})

// When colors are changed, function getColor() in text-tile.components needs to be updated
export class ChartColorService {
  public static readonly BLUE_COLORS: string[] = [
    SchenckColors.SCHENCK_COLOR_BLUE,
    SchenckColors.SCHENCK_COLOR_BLUE_LIGHT,
    SchenckColors.SCHENCK_COLOR_BLUE_LIGHTER,
    SchenckColors.SCHENCK_COLOR_BLUE_LIGHTEST,
    SchenckColors.SCHENCK_COLOR_BLUE_BRIGHT,
    SchenckColors.SCHENCK_COLOR_BLUE_BRIGHTEST,
  ];

  public static readonly BLUE_COLORS_DARK_MODE: string[] = [
    SchenckColors.SCHENCK_COLOR_BLUE_LIGHTER,
    SchenckColors.SCHENCK_COLOR_BLUE_LIGHTEST,
    SchenckColors.SCHENCK_COLOR_BLUE_BRIGHT,
    SchenckColors.SCHENCK_COLOR_BLUE_BRIGHTEST,
  ];

  public static readonly GREEN_COLORS: string[] = [
    SchenckColors.SCHENCK_COLOR_GREEN,
    SchenckColors.SCHENCK_COLOR_GREEN_DARK,
    SchenckColors.SCHENCK_COLOR_GREEN_BRIGHT,
    SchenckColors.SCHENCK_COLOR_GREEN_BRIGHTER,
    '#1e8071',
    '#176055',
  ];

  public static readonly GREEN_COLORS_DARK_MODE: string[] = [
    SchenckColors.SCHENCK_COLOR_GREEN_BRIGHT,
    SchenckColors.SCHENCK_COLOR_GREEN_BRIGHT,
    SchenckColors.SCHENCK_COLOR_GREEN_BRIGHTER,
    SchenckColors.SCHENCK_COLOR_GREEN_BRIGHTER,
  ];

  public static readonly RED_COLORS: string[] = [
    SchenckColors.SCHENCK_COLOR_RED,
    SchenckColors.SCHENCK_COLOR_RED_LIGHT,
    SchenckColors.SCHENCK_COLOR_PINK,
    SchenckColors.SCHENCK_COLOR_PINK_LIGHTER,
  ];

  public static readonly RED_COLORS_DARK_MODE: string[] = [
    SchenckColors.SCHENCK_COLOR_RED_LIGHT,
    SchenckColors.SCHENCK_COLOR_RED_LIGHT,
    SchenckColors.SCHENCK_COLOR_PINK_LIGHTER,
    SchenckColors.SCHENCK_COLOR_PINK_LIGHTER,
  ];

  public static readonly GRAY_COLORS: string[] = [
    SchenckColors.SCHENCK_COLOR_GRAY,
    SchenckColors.SCHENCK_COLOR_GRAY_LIGHT,
    SchenckColors.SCHENCK_COLOR_GRAY_LIGHTER,
    SchenckColors.SCHENCK_COLOR_GRAY_LIGHTEST,
  ];

  public static readonly GRAY_COLORS_DARK_MODE: string[] = [
    SchenckColors.SCHENCK_COLOR_GRAY_LIGHTER,
    SchenckColors.SCHENCK_COLOR_GRAY_LIGHTEST,
    SchenckColors.SCHENCK_COLOR_GRAY_BRIGHT,
    SchenckColors.SCHENCK_COLOR_GRAY_BRIGHTER,
  ];

  public static getColors(color: ChartColors, darkMode = false): string[] {
    return ChartColorService[darkMode ? `${color}_COLORS_DARK_MODE` : `${color}_COLORS`];
  }

  public static opModeToColorMapping: Record<string, (darkMode?: boolean) => string> = {
    0: () => '#84bd00', // 'MC_OP_Automatic',
    1: () => 'brown', // 'MC_OP_Standalone-Operation',
    2: () => '#ffbf00', // 'MC_OP_Manual',
    3: (darkMode) => ChartColorService.getColors(ChartColors.RED, darkMode)[0], // 'MC_OP_Error',
    4: () => 'blue', // 'MC_OP_Step-Mode',
    5: () => 'pink', // 'MC_OP_Setup',
    6: () => '#f09432', // 'MC_OP_Disconnected',
    7: () => 'grey', // 'MC_OP_Undefined',
  };

  public static eventToColorMapping: Record<string, (darkMode?: boolean) => string> = {
    Critical: (darkMode) => ChartColorService.getColors(ChartColors.RED, darkMode)[0],
    Major: () => '#f09432', // orange from invision
    Minor: () => '#ffbf00', // @schenck-color-orange
    Warning: () => '#84bd00', // @schenck-color-green
  };

  /**
   * Returns the appropriate color for a line chart series
   * The name of the series provides the information, wether its a co
   *
   * @param name
   * Name of the line chart series
   * has the following structure: name-assetnumber-planenumber
   * name can either be 'initialUnbalance' | 'residualUnbalance' | 'tolerance'
   * assetnumber can either be: 0 for main | 1 for compare
   * planenumber can be 0 or any positive integer. Depends on the rotor type
   * for example: initialUnbalance-0-1 (initialUnbalance from plane 1 of main asset)
   *
   * @param noOfPlanes
   * Number of Planes. Is needed to calculate the color array index
   */
  public getMultiTrendColors(name: string, noOfPlanes: number, darkMode = false): string {
    let color = '';

    const nameParts = name.split('-');
    const seriesName = nameParts[0];
    const asset: number = +nameParts[1];
    const plane: number = +nameParts[2];

    // the index for the color array
    // if we want the colors for the compare asset we don't want to start at index 0,
    // because these colors are already in use for the main asset
    // the index is not needed for the tolerance, because the tolerance remains the same for the compare asset
    const index = plane + 1 + asset * noOfPlanes - 1;

    if (seriesName.includes('tolerance')) {
      color = ChartColorService.getColors(ChartColors.GREEN, darkMode)[plane];
    } else if (seriesName.includes('initial')) {
      color = ChartColorService.getColors(ChartColors.BLUE, darkMode)[index];
    } else if (seriesName.includes('residual')) {
      color = ChartColorService.getColors(ChartColors.GRAY, darkMode)[index];
    }

    return color;
  }

  public getColorPairs(count: number, darkMode = false): string[] {
    const colors: string[] = [];
    // divide by 2 because we want pairs
    for (let i = 0; i < count / 2; i++) {
      const color = this.getColorPair(i, darkMode);
      colors.push(...color);
    }
    return colors;
  }

  getBarLineColors(countMachines: number, darkMode = false): string[] {
    const pair = [
      ChartColorService.getColors(ChartColors.BLUE, darkMode)[1],
      ChartColorService.getColors(ChartColors.BLUE, darkMode)[0],
    ];

    if (countMachines > 1) {
      pair.push(
        ChartColorService.getColors(ChartColors.GRAY, darkMode)[1],
        ChartColorService.getColors(ChartColors.GRAY, darkMode)[0]
      );
    }

    return pair;
  }

  public getColorPair(i: number, revert?: boolean, darkMode = false): string[] {
    return revert
      ? [
          ChartColorService.getColors(ChartColors.GRAY, darkMode)[i],
          ChartColorService.getColors(ChartColors.BLUE, darkMode)[i],
        ]
      : [
          ChartColorService.getColors(ChartColors.BLUE, darkMode)[i],
          ChartColorService.getColors(ChartColors.GRAY, darkMode)[i],
        ];
  }

  public getBalancingRunColor(measureIndex: number, valueIndex: number, darkMode = false): string {
    const mainColors = [];
    const compareColors = [];
    // has always 5 values : 1, 2, 3, >3, NIT
    for (let i = 0; i < 4; i++) {
      mainColors.push(ChartColorService.getColors(ChartColors.BLUE, darkMode)[0]);
      compareColors.push(ChartColorService.getColors(ChartColors.GRAY, darkMode)[0]);
    }
    // NIT must be red
    mainColors.push(ChartColorService.getColors(ChartColors.RED, darkMode)[0]);
    compareColors.push(ChartColorService.getColors(ChartColors.RED, darkMode)[2]);

    const colors = [mainColors, compareColors];
    return colors[measureIndex][valueIndex];
  }
}
